/**
 * Principal Policy Book Page
 * Dynamic policy management system with 13 domains
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ResponsiveLayout from '../components/ResponsiveLayout';
import { principalNavItems } from '../config/principalNav';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Book, CheckCircle, ChevronDown, ChevronRight, Clock, Cpu, Download, Edit2, FileText, Globe, History, IndianRupee, Lock, MessageCircle, Plus, Save, Scale, Search, Server, Settings, Shield, Trash2, UserCheck, UserCog, Users, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for domains
const domainIcons = {
  shield: Shield,
  lock: Lock,
  'user-cog': UserCog,
  users: Users,
  'check-circle': CheckCircle,
  clock: Clock,
  'message-circle': MessageCircle,
  'indian-rupee': IndianRupee,
  'file-text': FileText,
  server: Server,
  scale: Scale,
  cpu: Cpu,
  settings: Settings,
  globe: Globe
};

// Color mapping
const domainColors = {
  red: 'bg-red-100 text-red-700 border-red-200',
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  orange: 'bg-orange-100 text-orange-700 border-orange-200',
  cyan: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  amber: 'bg-amber-100 text-amber-700 border-amber-200',
  slate: 'bg-slate-100 text-slate-700 border-slate-200',
  gray: 'bg-slate-100 text-slate-700 border-slate-200',
  violet: 'bg-violet-100 text-violet-700 border-violet-200',
  rose: 'bg-rose-100 text-rose-700 border-rose-200',
  teal: 'bg-teal-100 text-teal-700 border-teal-200'
};

const PrincipalPolicyBook = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [principalName, setPrincipalName] = useState('Principal');

  const [loading, setLoading] = useState(true);
  const [domains, setDomains] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [byDomain, setByDomain] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedDomains, setExpandedDomains] = useState({});
  
  // Edit/Add modal
  const [showEditor, setShowEditor] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [editorForm, setEditorForm] = useState({
    domain_id: '',
    title: '',
    content: '',
    summary: '',
    applies_to: 'all',
    severity: 'standard',
    enforcement: 'mandatory',
    effective_date: new Date().toISOString().split('T')[0],
    country_code: '',
    inject_into: []
  });
  const [saving, setSaving] = useState(false);

  // Injection targets
  const INJECTION_TARGETS = [
    { id: 'call_analysis', label: 'Call Analysis', description: 'Coaching reports for observers' },
    { id: 'daily_insight', label: 'Daily Insight', description: 'Parent-facing daily summaries' },
    { id: 'monthly_report', label: 'Monthly Report', description: 'Monthly diagnostic reports' },
    { id: 'observer_handbook', label: 'Observer Handbook', description: 'Observer training materials' }
  ];

  const getToken = useCallback(() => localStorage.getItem('principal_token'), []);

  const handleLogout = () => {
    localStorage.removeItem('principal_token');
    navigate('/principal/login');
  };

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      if (!token) {
        navigate('/principal/login');
        return;
      }
      // Fetch principal name once
      axios.get(`${BACKEND_URL}/api/principal/dashboard?token=${token}`)
        .then(r => setPrincipalName(r.data?.principal?.name || 'Principal'))
        .catch(() => {});
      
      const [domainsRes, policiesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/principal/policy-domains`, { params: { token } }),
        axios.get(`${BACKEND_URL}/api/principal/policies`, { params: { token, search: searchQuery || undefined } })
      ]);
      
      setDomains(domainsRes.data.domains || []);
      setPolicies(policiesRes.data.policies || []);
      setByDomain(policiesRes.data.by_domain || []);
      
      // Auto-expand domains with policies
      const expanded = {};
      (policiesRes.data.by_domain || []).forEach(d => {
        if (d.policies?.length > 0) {
          expanded[d.id] = true;
        }
      });
      setExpandedDomains(prev => ({ ...prev, ...expanded }));
      
    } catch (error) {
      console.error('Load error:', error);
      toast({ title: "Error", description: "Failed to load policies", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [navigate, toast, getToken, searchQuery]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Create/Update policy
  const handleSave = async () => {
    if (!editorForm.title || !editorForm.content || !editorForm.domain_id) {
      toast({ title: "Error", description: "Please fill required fields", variant: "destructive" });
      return;
    }
    
    setSaving(true);
    try {
      const token = getToken();
      const formData = {
        ...editorForm,
        inject_into: editorForm.inject_into.join(',')
      };
      
      if (editingPolicy) {
        // Update
        await axios.put(`${BACKEND_URL}/api/principal/policy/${editingPolicy.id}`, null, {
          params: { token, ...formData, change_note: "Updated via Policy Book" }
        });
        toast({ title: "Success", description: "Policy updated and propagated to all connected systems" });
      } else {
        // Create
        await axios.post(`${BACKEND_URL}/api/principal/policy`, null, {
          params: { token, ...formData }
        });
        toast({ title: "Success", description: "Policy created" });
      }
      
      setShowEditor(false);
      setEditingPolicy(null);
      loadData();
    } catch (error) {
      console.error('Save error:', error);
      toast({ title: "Error", description: "Failed to save policy", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  // Archive policy
  const handleArchive = async (policyId) => {
    if (!window.confirm('Are you sure you want to archive this policy?')) return;
    
    try {
      const token = getToken();
      await axios.delete(`${BACKEND_URL}/api/principal/policy/${policyId}`, {
        params: { token, reason: "Archived by admin" }
      });
      toast({ title: "Success", description: "Policy archived" });
      loadData();
      setSelectedPolicy(null);
    } catch (error) {
      console.error('Archive error:', error);
      toast({ title: "Error", description: "Failed to archive policy", variant: "destructive" });
    }
  };

  // Open editor
  const openEditor = (policy = null, domainId = null) => {
    if (policy) {
      setEditingPolicy(policy);
      setEditorForm({
        domain_id: policy.domain_id,
        title: policy.title,
        content: policy.content,
        summary: policy.summary || '',
        applies_to: policy.applies_to || 'all',
        severity: policy.severity || 'standard',
        enforcement: policy.enforcement || 'mandatory',
        effective_date: policy.effective_date || new Date().toISOString().split('T')[0],
        country_code: policy.country_code || '',
        inject_into: policy.inject_into || []
      });
    } else {
      setEditingPolicy(null);
      setEditorForm({
        domain_id: domainId || '',
        title: '',
        content: '',
        summary: '',
        applies_to: 'all',
        severity: 'standard',
        enforcement: 'mandatory',
        effective_date: new Date().toISOString().split('T')[0],
        country_code: '',
        inject_into: []
      });
    }
    setShowEditor(true);
  };

  // Toggle domain expansion
  const toggleDomain = (domainId) => {
    setExpandedDomains(prev => ({
      ...prev,
      [domainId]: !prev[domainId]
    }));
  };

  // Severity badge
  const SeverityBadge = ({ severity }) => {
    const styles = {
      critical: 'bg-red-100 text-red-700',
      high: 'bg-orange-100 text-orange-700',
      standard: 'bg-blue-100 text-blue-700',
      informational: 'bg-slate-100 text-slate-600'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[severity] || styles.standard}`}>
        {severity}
      </span>
    );
  };

  // Enforcement badge
  const EnforcementBadge = ({ enforcement }) => {
    const styles = {
      mandatory: 'bg-red-50 text-red-600 border-red-200',
      recommended: 'bg-amber-50 text-amber-600 border-amber-200',
      optional: 'bg-green-50 text-green-600 border-green-200'
    };
    return (
      <span className={`px-2 py-0.5 rounded border text-xs ${styles[enforcement] || styles.mandatory}`}>
        {enforcement}
      </span>
    );
  };

  if (loading && domains.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-sanjaya-navy/20 border-t-sanjaya-navy rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-base sm:text-lg text-slate-600 font-medium">Loading policies...</p>
        </div>
      </div>
    );
  }

  return (
    <ResponsiveLayout navItems={principalNavItems} userRole="principal" userName={principalName} onLogout={handleLogout}>
    <div className="font-body" data-testid="policy-book-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-sanjaya-navy flex items-center gap-2 sm:gap-3">
            <Book className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-sanjaya-navy" />
            Policy Book
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">Manage platform policies across 13 domains</p>
        </div>
        <div className="flex gap-2 self-start sm:self-auto">
          <Button variant="outline" onClick={loadData} className="border-sanjaya-navy/30 text-sanjaya-navy">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
          <Button onClick={() => openEditor()} className="bg-sanjaya-navy hover:bg-sanjaya-navy-dark">
            <Plus className="w-4 h-4 mr-2" /> New Policy
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="mb-5 sm:mb-6 border-2 border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search policies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadData()}
                className="pl-10 focus:border-sanjaya-navy"
                data-testid="policy-search"
              />
            </div>
            <Button variant="outline" onClick={() => { setSearchQuery(''); loadData(); }} className="border-slate-300">
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5 sm:mb-6">
        <div className="bg-sanjaya-navy text-white rounded-xl p-3 sm:p-4">
          <p className="text-xs sm:text-sm text-white/80">Total Policies</p>
          <p className="text-xl sm:text-2xl font-bold font-heading">{policies.length}</p>
        </div>
        <div className="bg-red-500 text-white rounded-xl p-3 sm:p-4">
          <p className="text-xs sm:text-sm text-white/80">Critical</p>
          <p className="text-xl sm:text-2xl font-bold font-heading">{policies.filter(p => p.severity === 'critical').length}</p>
        </div>
        <div className="bg-sanjaya-orange text-sanjaya-navy rounded-xl p-3 sm:p-4">
          <p className="text-xs sm:text-sm text-sanjaya-navy/80">Mandatory</p>
          <p className="text-xl sm:text-2xl font-bold font-heading">{policies.filter(p => p.enforcement === 'mandatory').length}</p>
        </div>
        <div className="bg-sanjaya-teal text-white rounded-xl p-3 sm:p-4">
          <p className="text-xs sm:text-sm text-white/80">Domains Active</p>
          <p className="text-xl sm:text-2xl font-bold font-heading">{byDomain.filter(d => d.policies?.length > 0).length} / 13</p>
        </div>
      </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Domain List */}
            <div className="lg:col-span-1 space-y-3">
              {byDomain.map((domain) => {
                const Icon = domainIcons[domain.icon] || Book;
                const colorClass = domainColors[domain.color] || domainColors.gray;
                const isExpanded = expandedDomains[domain.id];
                const policyCount = domain.policies?.length || 0;

                return (
                  <Card key={domain.id} className="overflow-hidden border-2 border-slate-200">
                    <div
                      className={`p-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                        selectedDomain?.id === domain.id ? 'bg-sanjaya-navy/5 border-l-4 border-l-sanjaya-navy' : ''
                      }`}
                      onClick={() => {
                        toggleDomain(domain.id);
                        setSelectedDomain(domain);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${colorClass}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="font-medium text-sm text-slate-800">{domain.code}. {domain.name}</p>
                            <p className="text-xs text-slate-500">{policyCount} policies</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); openEditor(null, domain.id); }}
                            className="h-6 w-6 p-0"
                          >
                            <Plus className="w-3 h-3" />
                          </Button>
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded policies list */}
                    {isExpanded && domain.policies?.length > 0 && (
                      <div className="border-t bg-slate-50 divide-y">
                        {domain.policies.map((policy) => (
                          <div
                            key={policy.id}
                            className={`p-2 pl-12 cursor-pointer hover:bg-white transition-colors ${
                              selectedPolicy?.id === policy.id ? 'bg-sanjaya-navy/10' : ''
                            }`}
                            onClick={() => setSelectedPolicy(policy)}
                          >
                            <div className="flex items-center justify-between">
                              <p className="text-sm text-slate-700 truncate">{policy.title}</p>
                              <SeverityBadge severity={policy.severity} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {isExpanded && (!domain.policies || domain.policies.length === 0) && (
                      <div className="border-t bg-slate-50 p-3 text-center">
                        <p className="text-sm text-slate-400">No policies in this domain</p>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => openEditor(null, domain.id)}
                          className="text-sanjaya-navy"
                        >
                          Add first policy
                        </Button>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>

            {/* Right: Policy Detail */}
            <div className="lg:col-span-2">
              {selectedPolicy ? (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">{selectedPolicy.title}</CardTitle>
                        <p className="text-sm text-slate-500 mt-1">{selectedPolicy.summary}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEditor(selectedPolicy)}>
                          <Edit2 className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleArchive(selectedPolicy.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Meta info */}
                    <div className="flex flex-wrap gap-3 mb-4">
                      <SeverityBadge severity={selectedPolicy.severity} />
                      <EnforcementBadge enforcement={selectedPolicy.enforcement} />
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                        Applies to: {selectedPolicy.applies_to}
                      </span>
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs flex items-center gap-1">
                        <History className="w-3 h-3" /> v{selectedPolicy.version}
                      </span>
                      {selectedPolicy.country_code && (
                        <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs flex items-center gap-1">
                          <Globe className="w-3 h-3" /> {selectedPolicy.country_code}
                        </span>
                      )}
                    </div>
                    
                    {/* Injection targets */}
                    {selectedPolicy.inject_into?.length > 0 && (
                      <div className="mb-4 p-3 bg-sanjaya-navy/5 rounded-xl border border-sanjaya-navy/20">
                        <p className="text-xs font-medium text-sanjaya-navy mb-2">Auto-injected into:</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedPolicy.inject_into.map(target => (
                            <span key={target} className="px-2 py-1 bg-sanjaya-navy/10 text-sanjaya-navy rounded text-xs font-medium">
                              {target.replace('_', ' ')}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Content */}
                    <div className="prose prose-sm max-w-none bg-slate-50 rounded-lg p-4 border">
                      <div className="whitespace-pre-wrap text-slate-700">
                        {selectedPolicy.content}
                      </div>
                    </div>
                    
                    {/* Footer meta */}
                    <div className="mt-4 pt-4 border-t flex items-center justify-between text-xs text-slate-500">
                      <span>Effective: {selectedPolicy.effective_date}</span>
                      <span>Last updated: {new Date(selectedPolicy.updated_at).toLocaleDateString()}</span>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-2 border-slate-200 bg-slate-50">
                  <CardContent className="p-12 text-center">
                    <Book className="w-16 h-16 mx-auto mb-4 text-sanjaya-navy/20" />
                    <p className="text-sanjaya-navy text-lg font-heading font-semibold">Select a policy to view details</p>
                    <p className="text-slate-400 text-sm mt-1">Or create a new policy in any domain</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
    </div>

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-sanjaya-navy">{editingPolicy ? 'Edit Policy' : 'New Policy'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowEditor(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Domain */}
              <div>
                <label className="text-sm font-medium">Domain *</label>
                <select
                  value={editorForm.domain_id}
                  onChange={(e) => setEditorForm({ ...editorForm, domain_id: e.target.value })}
                  className="w-full border rounded-md px-3 py-2 mt-1"
                  disabled={!!editingPolicy}
                >
                  <option value="">Select Domain</option>
                  {domains.map(d => (
                    <option key={d.id} value={d.id}>{d.code}. {d.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Title */}
              <div>
                <label className="text-sm font-medium">Policy Title *</label>
                <Input
                  value={editorForm.title}
                  onChange={(e) => setEditorForm({ ...editorForm, title: e.target.value })}
                  placeholder="e.g., Mandatory Reporting Requirements"
                  className="mt-1"
                />
              </div>
              
              {/* Summary */}
              <div>
                <label className="text-sm font-medium">Summary (short description)</label>
                <Input
                  value={editorForm.summary}
                  onChange={(e) => setEditorForm({ ...editorForm, summary: e.target.value })}
                  placeholder="Brief description for listings"
                  className="mt-1"
                />
              </div>
              
              {/* Content */}
              <div>
                <label className="text-sm font-medium">Policy Content *</label>
                <textarea
                  value={editorForm.content}
                  onChange={(e) => setEditorForm({ ...editorForm, content: e.target.value })}
                  className="w-full border rounded-md px-3 py-2 mt-1 min-h-[200px] font-mono text-sm"
                  placeholder="Full policy text..."
                />
              </div>
              
              {/* Row: Severity, Enforcement, Applies To */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium">Severity</label>
                  <select
                    value={editorForm.severity}
                    onChange={(e) => setEditorForm({ ...editorForm, severity: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 mt-1"
                  >
                    <option value="critical">🔴 Critical</option>
                    <option value="high">🟠 High</option>
                    <option value="standard">🔵 Standard</option>
                    <option value="informational">⚪ Informational</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Enforcement</label>
                  <select
                    value={editorForm.enforcement}
                    onChange={(e) => setEditorForm({ ...editorForm, enforcement: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 mt-1"
                  >
                    <option value="mandatory">Mandatory</option>
                    <option value="recommended">Recommended</option>
                    <option value="optional">Optional</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Applies To</label>
                  <select
                    value={editorForm.applies_to}
                    onChange={(e) => setEditorForm({ ...editorForm, applies_to: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 mt-1"
                  >
                    <option value="all">All Users</option>
                    <option value="observers">Observers Only</option>
                    <option value="principals">Principals Only</option>
                    <option value="parents">Parents Only</option>
                  </select>
                </div>
              </div>
              
              {/* Row: Effective Date, Country */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Effective Date</label>
                  <Input
                    type="date"
                    value={editorForm.effective_date}
                    onChange={(e) => setEditorForm({ ...editorForm, effective_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Country Code (optional)</label>
                  <Input
                    value={editorForm.country_code}
                    onChange={(e) => setEditorForm({ ...editorForm, country_code: e.target.value })}
                    placeholder="e.g., IN, US, UK"
                    className="mt-1"
                  />
                </div>
              </div>
              
              {/* Auto-inject into AI systems */}
              <div className="bg-sanjaya-navy/5 p-4 rounded-xl border border-sanjaya-navy/20">
                <label className="text-sm font-medium text-sanjaya-navy flex items-center gap-2 mb-3">
                  Auto-Inject Into (policy will be included in AI prompts)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {INJECTION_TARGETS.map(target => (
                    <label
                      key={target.id}
                      className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                        editorForm.inject_into.includes(target.id)
                          ? 'bg-sanjaya-navy/10 border-sanjaya-navy'
                          : 'bg-white border-slate-200 hover:border-sanjaya-navy/30'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={editorForm.inject_into.includes(target.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditorForm({ ...editorForm, inject_into: [...editorForm.inject_into, target.id] });
                          } else {
                            setEditorForm({ ...editorForm, inject_into: editorForm.inject_into.filter(t => t !== target.id) });
                          }
                        }}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-medium text-sm text-slate-800">{target.label}</p>
                        <p className="text-xs text-slate-500">{target.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-sanjaya-navy/70 mt-2">
                  When selected, this policy will automatically be included in AI-generated content.
                </p>
              </div>
              
              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowEditor(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-sanjaya-navy hover:bg-sanjaya-navy-dark"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : editingPolicy ? 'Update Policy' : 'Create Policy'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </ResponsiveLayout>
  );
};

export default PrincipalPolicyBook;
